import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { create } from '../api';

export const COUNTRY_CODES = [
  { value: '+61', label: 'Australia (+61)' },
  { value: '+64', label: 'New Zealand (+64)' },
  { value: '+1', label: 'United States/Canada (+1)' },
  { value: '+44', label: 'United Kingdom (+44)' },
  { value: '+65', label: 'Singapore (+65)' },
  { value: '+91', label: 'India (+91)' },
];

const DEFAULT_WIZARD = {
  step: 1,
  selectedMatchId: null,
  matchResult: { status: 'idle', candidates: [] },
  local: {
    profileImage: '',
    name: '',
    supplierId: null,
    phoneCountryCode: '+61',
    phoneNational: '',
    email: '',
    notes: '',
  },
};

const normalizePhoneDigits = (phoneValue) => String(phoneValue || '').replace(/\D/g, '');
const normalizeEmail = (email) => (email || '').trim().toLowerCase() || null;
const isValidAustralianMobile = (phoneNational) => /^04\d{8}$/.test((phoneNational || '').replace(/\s+/g, ''));
const isValidInternationalMobileLength = (phoneNational) => /^\d{8,15}$/.test((phoneNational || '').replace(/\s+/g, ''));

const applyMatchedCandidateToLocal = (candidate, currentLocal) => {
  if (!candidate) return currentLocal;
  return {
    ...currentLocal,
    name: currentLocal.name || candidate.name || '',
    email: currentLocal.email || candidate.email || '',
    profileImage: currentLocal.profileImage || candidate.profileImage || '',
    supplierId: currentLocal.supplierId || candidate.suggestedLocalSupplierId || null,
  };
};

const normalizePhone = (countryCode, phoneNational) => {
  const digitsNationalRaw = (phoneNational || '').replace(/\s+/g, '');
  let digitsNational = digitsNationalRaw;
  if ((countryCode || '+61') === '+61' && digitsNational.startsWith('0')) digitsNational = digitsNational.slice(1);
  const phoneE164 = digitsNational ? `${countryCode || '+61'}${digitsNational}` : null;
  return { phoneE164, phoneDigitsKey: phoneE164 ? phoneE164.replace(/\D/g, '') : null };
};

export const useContractorWizard = ({ contractors = [], suppliers = [], onSaved }) => {
  const [showWizard, setShowWizard] = useState(false);
  const [wizard, setWizard] = useState(DEFAULT_WIZARD);
  const [wizardErrors, setWizardErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const profileFileInputRef = useRef(null);

  const supplierOptions = useMemo(() => suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })), [suppliers]);

  const localFormFields = useMemo(() => ([
    { field: 'name', label: 'Name', type: 'text', size: 12, required: true, maxLength: 100 },
    { field: 'supplierId', label: 'Select Supplier', type: 'select', size: 12, required: true, options: supplierOptions, isClearable: true },
    { field: 'phoneCountryCode', label: 'Country Calling Code', type: 'select', size: 4, options: COUNTRY_CODES, isClearable: false },
    {
      field: 'phoneNational', label: 'Phone', type: 'text', size: 8,
      onChange: (event) => setWizard((prev) => ({ ...prev, local: { ...prev.local, phoneNational: (event.target.value || '').replace(/[^\d ]/g, '') } })),
      onBlur: () => setWizard((prev) => ({ ...prev, local: { ...prev.local, phoneNational: (prev.local.phoneNational || '').replace(/\s+/g, ' ').trim() } })),
    },
    { field: 'email', label: 'Email', type: 'text', size: 12 },
    { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
  ]), [supplierOptions]);

  const setWizardLocalForm = useCallback((next) => {
    setWizard((prev) => ({ ...prev, local: typeof next === 'function' ? next(prev.local) : next }));
  }, []);

  const { renderField: renderLocalField } = useFormBuilder(localFormFields, wizard.local, setWizardLocalForm, wizardErrors);

  const wizardPhoneFields = useMemo(() => localFormFields.filter((f) => ['phoneCountryCode', 'phoneNational'].includes(f.field)), [localFormFields]);
  const wizardDetailsFields = useMemo(() => localFormFields.filter((f) => ['name', 'supplierId', 'email', 'notes'].includes(f.field)), [localFormFields]);
  const selectedCandidate = wizard.matchResult.candidates.find((candidate) => candidate.id === wizard.selectedMatchId);

  const runValidationStep = async () => {
    const localPhone = (wizard.local.phoneNational || '').trim();
    if (!localPhone) return setWizardErrors({ phoneNational: 'Enter a phone number.' });
    if (wizard.local.phoneCountryCode === '+61' && !isValidAustralianMobile(localPhone)) return setWizardErrors({ phoneNational: 'Enter a valid Australian mobile number (04xxxxxxxx).' });
    if (wizard.local.phoneCountryCode !== '+61' && !isValidInternationalMobileLength(localPhone)) return setWizardErrors({ phoneNational: 'Enter a valid mobile number.' });

    const { phoneDigitsKey } = normalizePhone(wizard.local.phoneCountryCode, localPhone);
    if (contractors.find((contractor) => normalizePhoneDigits(contractor.phone) === phoneDigitsKey)) {
      return setWizardErrors({ phoneNational: 'Contractor already exists.' });
    }

    setIsValidating(true);
    try {
      const list = await create('contractors/validate-global', { phoneDigitsKey: phoneDigitsKey || undefined });
      const candidates = Array.isArray(list) ? list : [];
      const selected = candidates.length === 1 ? candidates[0] : null;
      setWizard((prev) => ({
        ...prev,
        step: 2,
        selectedMatchId: selected?.id || null,
        matchResult: { status: candidates.length === 0 ? 'none' : candidates.length === 1 ? 'single' : 'multiple', candidates },
        local: applyMatchedCandidateToLocal(selected, { ...prev.local }),
      }));
      setWizardErrors({});
    } catch (error) {
      alert(error.message || 'Unable to validate against global contractors.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    const { phoneE164 } = normalizePhone(wizard.local.phoneCountryCode, wizard.local.phoneNational);
    const hasMatch = wizard.matchResult.status === 'single' || wizard.matchResult.status === 'multiple';
    const mode = wizard.matchResult.status === 'none' ? 'create_new_global' : hasMatch ? 'link_existing' : 'local_only';
    const payload = {
      mode,
      matchedGlobalId: mode === 'link_existing' ? wizard.selectedMatchId : null,
      supplierId: wizard.local.supplierId,
      profileImage: wizard.local.profileImage || selectedCandidate?.profileImage || '',
      name: (wizard.local.name || selectedCandidate?.name || '').trim(),
      phone: phoneE164,
      phoneCountryCode: wizard.local.phoneCountryCode,
      phoneNational: wizard.local.phoneNational,
      email: normalizeEmail(wizard.local.email || selectedCandidate?.email),
      notes: wizard.local.notes || '',
      globalData: mode === 'create_new_global' ? {
        profileImage: wizard.local.profileImage || '',
        name: (wizard.local.name || '').trim(),
        supplierId: wizard.local.supplierId,
        phone: phoneE164,
        email: normalizeEmail(wizard.local.email || selectedCandidate?.email),
      } : null,
    };

    const nextErrors = {};
    if (!payload.supplierId) nextErrors.supplierId = 'Supplier is required.';
    if (!payload.phone) nextErrors.phoneNational = 'Enter a phone number.';
    if (payload.mode === 'link_existing' && !payload.matchedGlobalId) nextErrors.selectedMatchId = 'Select a global contractor to link.';
    if (payload.mode === 'create_new_global' && !payload.name) nextErrors.name = 'Name is required.';
    if (Object.keys(nextErrors).length) return setWizardErrors(nextErrors);

    setIsSaving(true);
    try {
      const createdContractor = await create('contractors', payload);
      if (onSaved) {
        await onSaved(createdContractor);
      }
      setShowWizard(false);
      setWizard(DEFAULT_WIZARD);
    } catch (error) {
      alert(error.message || 'Unable to save contractor.');
    } finally {
      setIsSaving(false);
    }
  };

  const uploadProfileImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/upload/modelimages`, { method: 'POST', body: formData, credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body?.key) throw new Error(body?.message || 'Unable to upload image.');
    setWizard((prev) => ({ ...prev, local: { ...prev.local, profileImage: body.key } }));
  };

  return {
    showWizard,
    openWizard: () => { setWizard(DEFAULT_WIZARD); setWizardErrors({}); setShowWizard(true); },
    closeWizard: () => setShowWizard(false),
    wizard,
    setWizard,
    wizardErrors,
    isSaving,
    isValidating,
    runValidationStep,
    handleSave,
    renderLocalField,
    wizardPhoneFields,
    wizardDetailsFields,
    selectedCandidate,
    profileFileInputRef,
    uploadProfileImage,
  };
};
